import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AppRegistration,
  Assessment,
  Close,
  Dashboard,
  Group,
  Inbox,
  MoreHoriz,
  PermContactCalendar,
  Phone,
  Storefront,
  TravelExplore,
  Work,
} from '@mui/icons-material';
import {
  Badge,
  BottomNavigation,
  BottomNavigationAction,
  Box,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import { UserRoles } from '@bandido/shared/types';
import { useAuth } from '@frontend/features/auth/hooks/useAuth';
import { useInbox } from '@frontend/features/inbox/InboxContext';
import { useCatalogAdminCapability } from '@frontend/features/hotel-catalog-admin/queries';
import { DASHBOARD_MOBILE_NAV_HEIGHT } from '@frontend/features/common/constants/layout';
import { routes } from '@frontend/router/routes';
import { dashboardReturnUrl } from '../navigationMemory';

export const DashboardMobileNavigation = () => {
  const { t } = useTranslation();
  const { ability, myProfile } = useAuth();
  const { hasInboxAccess, unreadCaseCount } = useInbox();
  const catalogAdmin = useCatalogAdminCapability();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [moreOpen, setMoreOpen] = useState(false);
  const primary = [
    {
      route: routes.dashboard.inbox,
      label: t('common.nav.inbox'),
      visible: hasInboxAccess,
      icon: (
        <Badge badgeContent={unreadCaseCount} color="error" max={99}>
          <Inbox />
        </Badge>
      ),
    },
    {
      route: routes.dashboard.cases,
      label: t('common.nav.cases'),
      visible: ability?.can('view', 'Cases'),
      icon: <Work />,
    },
    {
      route: routes.dashboard.clients,
      label: t('common.nav.clients'),
      visible: ability?.can('view', 'Clients'),
      icon: <PermContactCalendar />,
    },
  ].filter((entry) => entry.visible);
  const secondary = [
    {
      route: routes.dashboard.index,
      label: t('common.nav.summary'),
      visible: ability?.can('view', 'Dashboard'),
      icon: <Dashboard />,
    },
    {
      route: routes.dashboard.performance,
      label: t('workflowPerformance.homeTitle'),
      visible: myProfile?.roles?.split(',').includes(UserRoles.ADMINS),
      icon: <Assessment />,
    },
    {
      route: routes.dashboard.travelBookings,
      label: t('common.nav.travelBookings'),
      visible: ability?.can('view', 'TravelServices'),
      icon: <TravelExplore />,
    },
    {
      route: routes.dashboard.calls,
      label: t('common.nav.calls'),
      visible: ability?.can('view', 'Calls'),
      icon: <Phone />,
    },
    {
      route: routes.dashboard.agencies,
      label: t('common.nav.agencies'),
      visible: ability?.can('view', 'Agencies'),
      icon: <Storefront />,
    },
    {
      route: routes.dashboard.users,
      label: t('common.nav.users'),
      visible: ability?.can('manage', 'Users'),
      icon: <Group />,
    },
    {
      route: routes.dashboard.content.hotel.list,
      label: t('hotels.contentManager.title'),
      visible: !catalogAdmin.data?.allowed && ability?.can('view', 'ContentManager'),
      icon: <AppRegistration />,
    },
  ].filter((entry) => entry.visible);
  const active =
    primary.find((entry) => pathname === entry.route || pathname.startsWith(`${entry.route}/`))?.route ?? 'more';

  return (
    <>
      <BottomNavigation
        component="nav"
        aria-label={t('common.nav.dashboardSections')}
        showLabels
        value={active}
        onChange={(_, route) =>
          route === 'more' ? setMoreOpen(true) : navigate(dashboardReturnUrl(myProfile?.id, route))
        }
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: (theme) => theme.zIndex.appBar,
          height: DASHBOARD_MOBILE_NAV_HEIGHT,
          pb: 'env(safe-area-inset-bottom, 0px)',
          borderTop: 1,
          borderColor: 'divider',
          '& .MuiBottomNavigationAction-root': { minWidth: 0, minHeight: 64, px: 1 },
          '& .MuiBottomNavigationAction-label, & .MuiBottomNavigationAction-label.Mui-selected': { fontSize: 12 },
        }}
      >
        {primary.map((entry) => (
          <BottomNavigationAction key={entry.route} value={entry.route} label={entry.label} icon={entry.icon} />
        ))}
        <BottomNavigationAction value="more" label={t('common.nav.moreSections')} icon={<MoreHoriz />} />
      </BottomNavigation>
      <Drawer
        anchor="bottom"
        open={moreOpen}
        onClose={() => setMoreOpen(false)}
        PaperProps={{
          sx: { maxHeight: '85dvh', borderRadius: '16px 16px 0 0', pb: 'env(safe-area-inset-bottom, 0px)' },
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" px={2} pt={1}>
          <Typography variant="h6">{t('common.nav.dashboard')}</Typography>
          <IconButton
            aria-label={t('common.close')}
            onClick={() => setMoreOpen(false)}
            sx={{ minWidth: 44, minHeight: 44 }}
          >
            <Close />
          </IconButton>
        </Box>
        <List>
          {secondary.map((entry) => (
            <ListItemButton
              key={entry.route}
              selected={pathname === entry.route}
              sx={{ minHeight: 52 }}
              onClick={() => {
                setMoreOpen(false);
                navigate(entry.route);
              }}
            >
              <ListItemIcon>{entry.icon}</ListItemIcon>
              <ListItemText primary={entry.label} />
            </ListItemButton>
          ))}
        </List>
      </Drawer>
    </>
  );
};
